/**
 * Копилот: вопрос по таблице — ответ по данным.
 *
 * Шарик в правом нижнем углу и окно над ним — помощник всегда под рукой и
 * никогда не занимает место в шапке: вопрос задают по ходу работы, глядя в
 * таблицу, и уходить с неё ради ответа незачем.
 *
 * Разговоры хранятся на сервере у каждого свои: к разбору возвращаются через
 * день-другой, а память вкладки живёт до обновления страницы. Открыли шарик —
 * продолжаете последний разговор; «+» заводит новый, часы показывают прошлые.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CopilotChat } from '@shared/types.ts'
import { IconClose, IconHistory, IconPlus, IconSend, IconTrash, RichText, Spinner } from '../../components/index.js'
import { CopilotMark } from './CopilotMark.js'
import { api, ApiRequestError } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { copilotTexts as t } from './texts.copilot.js'
import './Copilot.css'

interface Turn {
  role: 'user' | 'assistant'
  content: string
}

/** «сегодня в 14:30», «вчера в 9:05», «12 октября» — как в переписке. */
function whenLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()
  if (sameDay(date, now)) return `сегодня в ${time}`
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, yesterday)) return `вчера в ${time}`
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

export function Copilot() {
  /* Док черновика стоит внизу: шарик поднимается над ним, а не прячется. */
  const draftCount = useApp((state) => state.overlay.count)

  const [open, setOpen] = useState(false)
  const [chatId, setChatId] = useState<number | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  /* Модель сперва думает и только потом пишет: показываем и ход мысли,
     и сколько уже идёт раздумье — молчащая крутилка пугает. */
  const [reason, setReason] = useState('')
  const [since, setSince] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const [historyOpen, setHistoryOpen] = useState(false)
  const [chats, setChats] = useState<CopilotChat[] | null>(null)
  const [restored, setRestored] = useState(false)

  const feed = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLTextAreaElement>(null)
  /* Человек отлистал ленту вверх — читает прошлое, и дёргать его вниз
     нельзя. Прокручиваем сами, только пока он у нижнего края. */
  const atBottom = useRef(true)
  const reasonBox = useRef<HTMLDivElement>(null)

  const loadChats = useCallback(async (): Promise<CopilotChat[]> => {
    try {
      const res = await api.copilotChats()
      setChats(res.chats)
      return res.chats
    } catch {
      setChats([])
      return []
    }
  }, [])

  const openChat = useCallback(async (id: number): Promise<void> => {
    setHistoryOpen(false)
    setError(null)
    setBusy(true)
    try {
      const res = await api.copilotChat(id)
      setChatId(res.chat.id)
      setTurns(res.messages.map((message) => ({ role: message.role, content: message.content })))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed)
    } finally {
      setBusy(false)
      input.current?.focus()
    }
  }, [])

  /* Первое открытие за сессию: подхватываем последний разговор — человек
     возвращается к тому, на чём остановился, а не к пустому экрану. */
  useEffect(() => {
    if (!open || restored) return
    setRestored(true)
    void (async () => {
      const list = await loadChats()
      const last = list[0]
      if (last && turns.length === 0) await openChat(last.id)
    })()
  }, [open, restored, loadChats, openChat, turns.length])

  useEffect(() => {
    if (open) input.current?.focus()
  }, [open])

  /* Поле ввода растёт под текст: высоту пересчитываем сами — у textarea её
     задаёт число строк, а оно постоянное. Потолок — шесть строк, дальше
     текст прокручивается внутри поля. */
  useEffect(() => {
    const el = input.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`
  }, [question, open])

  /* Секундомер раздумья: раз в секунду, пока ответа ещё нет. */
  useEffect(() => {
    if (since === null) return
    setElapsed(0)
    const timer = window.setInterval(() => {
      setElapsed(Math.round((Date.now() - since) / 1000))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [since])

  /* Новый ответ длиннее экрана — прокручиваем к нему, как в любой переписке.
     Но только если человек и так внизу: иначе он читает прошлое, а лента
     выдёргивает его обратно. */
  useEffect(() => {
    if (!atBottom.current) return
    feed.current?.scrollTo({ top: feed.current.scrollHeight })
  }, [turns, busy, reason])

  /* Лента размышления едет сама: это бегущая строка, а не текст для чтения. */
  useEffect(() => {
    const box = reasonBox.current
    if (box) box.scrollTop = box.scrollHeight
  }, [reason])

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      if (historyOpen) setHistoryOpen(false)
      else setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, historyOpen])

  function startNew(): void {
    setChatId(null)
    setTurns([])
    setError(null)
    setHistoryOpen(false)
    input.current?.focus()
  }

  async function removeChat(id: number): Promise<void> {
    try {
      await api.copilotDeleteChat(id)
      setChats((previous) => (previous ?? []).filter((chat) => chat.id !== id))
      if (id === chatId) startNew()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed)
    }
  }

  async function ask(text: string): Promise<void> {
    const asked = text.trim()
    if (asked === '' || busy) return
    setQuestion('')
    setError(null)
    setTurns((previous) => [...previous, { role: 'user', content: asked }])
    setBusy(true)
    setReason('')
    setSince(Date.now())
    atBottom.current = true
    /* Пустой ответ появляется сразу и наполняется буквами по мере прихода:
       ждать минуту на крутилке, не зная, пишет ли вообще, — мучение. */
    let started = false
    try {
      const res = await api.copilotAsk(
        chatId ? { question: asked, chatId } : { question: asked },
        (piece) => {
          setTurns((previous) => {
            if (!started) {
              started = true
              return [...previous, { role: 'assistant', content: piece }]
            }
            const last = previous[previous.length - 1]
            if (!last || last.role !== 'assistant') return [...previous, { role: 'assistant', content: piece }]
            return [...previous.slice(0, -1), { role: 'assistant', content: last.content + piece }]
          })
        },
        (piece) => setReason((previous) => (previous + piece).slice(-2000)),
      )
      setChatId(res.chatId)
      /* Список разговоров устарел: сменился порядок, а у нового появилось имя. */
      setChats(null)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed)
    } finally {
      setBusy(false)
      setSince(null)
      setReason('')
      input.current?.focus()
    }
  }

  async function toggleHistory(): Promise<void> {
    const next = !historyOpen
    setHistoryOpen(next)
    if (next && chats === null) await loadChats()
  }

  return (
    <div className={draftCount > 0 ? 'copilot-root copilot-root--dock' : 'copilot-root'}>
      {open ? (
        <section className="copilot" role="dialog" aria-label={t.open}>
          <header className="copilot__head">
            <span className="copilot__mark" aria-hidden="true"><CopilotMark size={20} /></span>
            <span className="copilot__id">
              <span className="copilot__title">{t.title}</span>
              <span className="copilot__status">{t.subtitle}</span>
            </span>
            <span className="copilot__actions">
              <button type="button" className="copilot__icon" aria-label={t.newChat} title={t.newChat} onClick={startNew}>
                <IconPlus size={20} />
              </button>
              <button
                type="button"
                className={historyOpen ? 'copilot__icon copilot__icon--on' : 'copilot__icon'}
                aria-label={t.history}
                title={t.history}
                aria-expanded={historyOpen}
                onClick={() => void toggleHistory()}
              >
                <IconHistory size={20} />
              </button>
              <button type="button" className="copilot__icon" aria-label={t.close} title={t.close} onClick={() => setOpen(false)}>
                <IconClose size={20} />
              </button>
            </span>

            {historyOpen ? (
              <div className="copilot__history">
                <div className="copilot__history-top">{t.history}</div>
                {chats === null ? (
                  <p className="copilot__history-empty t-meta">{t.historyLoading}</p>
                ) : chats.length === 0 ? (
                  <p className="copilot__history-empty t-meta">{t.historyEmpty}</p>
                ) : (
                  chats.map((chat) => (
                    <div
                      key={chat.id}
                      className={chat.id === chatId ? 'copilot__chat copilot__chat--on' : 'copilot__chat'}
                    >
                      <button type="button" className="copilot__chat-open" onClick={() => void openChat(chat.id)}>
                        <span className="copilot__chat-title">{chat.title}</span>
                        <span className="copilot__chat-meta t-meta">{whenLabel(chat.updatedAt)}</span>
                      </button>
                      <button
                        type="button"
                        className="copilot__chat-del"
                        aria-label={t.deleteChat}
                        title={t.deleteChat}
                        onClick={() => void removeChat(chat.id)}
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </header>

          <div
            className="copilot__feed"
            ref={feed}
            onScroll={(event) => {
              const box = event.currentTarget
              atBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 60
            }}
          >
            {turns.length === 0 ? (
              <div className="copilot__intro">
                <p className="copilot__intro-text">{t.intro}</p>
                <div className="copilot__samples">
                  {t.samples.map((sample) => (
                    <button
                      key={sample}
                      type="button"
                      className="copilot__sample"
                      onClick={() => void ask(sample)}
                    >
                      {sample}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {turns.map((turn, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className={turn.role === 'user' ? 'copilot__turn copilot__turn--mine' : 'copilot__turn'}
              >
                <div className="copilot__bubble">
                  {turn.role === 'user'
                    ? <p>{turn.content}</p>
                    : <RichText text={turn.content} />}
                </div>
              </div>
            ))}

            {busy && turns[turns.length - 1]?.role !== 'assistant' ? (
              <div className="copilot__turn">
                <div className="copilot__bubble copilot__bubble--wait">
                  <span className="copilot__wait-head">
                    <Spinner size={14} />
                    <span>{reason === '' ? t.thinking : t.pondering}</span>
                    <span className="copilot__timer">{t.elapsed(elapsed)}</span>
                  </span>
                  {reason !== '' ? (
                    <div className="copilot__reason" ref={reasonBox}>{reason}</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {error ? <p className="copilot__error">{error}</p> : null}
          </div>

          <form
            className="copilot__composer"
            onSubmit={(event) => { event.preventDefault(); void ask(question) }}
          >
            <textarea
              ref={input}
              className="copilot__input"
              rows={1}
              value={question}
              maxLength={2000}
              placeholder={t.placeholder}
              aria-label={t.placeholder}
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void ask(question)
                }
              }}
            />
            <button
              type="submit"
              className="copilot__send"
              disabled={question.trim() === '' || busy}
              aria-label={t.send}
            >
              <IconSend size={20} />
            </button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className={open ? 'copilot-orb copilot-orb--on' : 'copilot-orb'}
        aria-label={open ? t.close : t.open}
        title={open ? t.close : t.open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="copilot-orb__fx" aria-hidden="true">
          <i className="d1" /><i className="d2" />
        </span>
        <span className="copilot-orb__glyph" aria-hidden="true">
          {open ? <IconClose size={20} /> : <CopilotMark size={30} />}
        </span>
      </button>
    </div>
  )
}
